import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SupportMessagesControllerService } from '@app/generated/services';
import { GuiParams } from '@app/shared/store/gui-params/gui-params-facade.service';
import { TranslateService } from '@ngx-translate/core';
import { RecaptchaComponent } from 'ng-recaptcha-2';
import { ToastrService } from 'ngx-toastr';
import { emailRegexp } from '@app-shared/util';
import { TrackingParametersComponent } from '@app/unauthenticated/trackingParameters.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppGtmService } from '@app/shared/services/gtm.service';

enum ResponseStatus {
  ok = 'OK',
  error = 'ERROR',
  notSend = 'NOT_SEND',
}

@Component({
  selector: 'app-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
})
export class ContactFormComponent extends TrackingParametersComponent implements OnInit, OnDestroy {
  @ViewChild('recaptchaRef') recaptchaRef!: RecaptchaComponent;
  recaptcha!: string;
  recaptchaSiteKey = '';
  responseStatus = ResponseStatus;
  response: ResponseStatus = ResponseStatus.notSend;
  supportForm!: UntypedFormGroup;
  email: string | null = null;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private supportMessageService: SupportMessagesControllerService,
    private formBuilder: UntypedFormBuilder,
    private toaster: ToastrService,
    private translate: TranslateService,
    private guiParams: GuiParams,
    private gtmService: AppGtmService,
  ) {
    super(platformId);
  }

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    this.email = urlParams.get('email');

    this.supportForm = this.formBuilder.group({
      email: [this.email, [Validators.required, Validators.pattern(emailRegexp)]],
      message: ['', [Validators.required]],
    });
    this.guiParams.guiParams$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((guiParams) => (this.recaptchaSiteKey = guiParams.supportRecaptchaSiteKey));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  recaptchaResolved(captchaResponse: string | null) {
    if (captchaResponse) {
      this.recaptcha = captchaResponse;
    }
  }

  onSubmit() {
    this.supportMessageService
      .addMessageUsingPost({
        body: {
          email: this.supportForm.value.email,
          message: this.supportForm.value.message,
          recaptcha: this.recaptcha,
        },
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.gtmService.pushTag({
          event: 'support_form_submitted',
          email_address: this.supportForm.value.email,
        });
        this.toaster.info(this.translate.instant('contact-form.message-sent-success'));
        this.resetSupportForm();
      });
  }

  resetSupportForm() {
    this.supportForm.reset();
    this.supportForm.controls.email.markAsPristine();
    this.supportForm.controls.email.markAsUntouched();
    this.recaptchaRef.reset();
  }

  isFieldInvalid(field: string): boolean {
    const fc = this.supportForm.get(field);
    return !!fc && !fc.valid && fc.touched && !fc.pristine;
  }
}
